import { Fragment, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  X,
  XCircle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { apiMutate, apiStreamClient, getApiErrorMessage, isApiError } from "~/utils/apiClient";
import { readStreamEvents } from "~/utils/streamReader";

interface CohortAccessRequest {
  id: string;
  created_by: string;
  updated_by: string | null;
  cohort_id: number;
  parent_dataset_id: string;
  child_dataset_id: string | null;
  intended_use: string;
  status: "pending" | "processing" | "approved" | "denied" | "failed" | "cancelled";
  created_at: string;
  updated_at: string;
}

interface PaginatedAccessRequests {
  items: CohortAccessRequest[];
  total: number;
  limit: number;
  offset: number;
}

const PAGE_SIZE = 25;

const STATUS_VARIANT = {
  pending: "warning",
  processing: "secondary",
  approved: "success",
  denied: "destructive",
  failed: "destructive",
  cancelled: "secondary",
} as const;

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

// ── Streaming types ──────────────────────────────────────────────────────────

interface ProgressStreamEvent {
  kind: "progress";
  message: string;
}

interface ResultStreamEvent {
  kind: "result";
  id: string;
  name: string;
}

interface ErrorStreamEvent {
  kind: "error";
  message: string;
}

type StreamEvent = ProgressStreamEvent | ResultStreamEvent | ErrorStreamEvent;

interface ProgressItem {
  message: string;
  status: "loading" | "done";
}

type AccessRequestStatus =
  | "pending"
  | "processing"
  | "approved"
  | "denied"
  | "failed"
  | "cancelled";
type ApprovalStreamStatus = "idle" | "streaming" | "done" | "error";

interface ApprovalPanelState {
  requestId: string;
  datasetName: string;
  streamStatus: ApprovalStreamStatus;
  progressItems: ProgressItem[];
  resultDatasetId: string | null;
  streamError: string | null;
}

// ── ApprovalPanel ────────────────────────────────────────────────────────────

interface ApprovalPanelProps {
  request: CohortAccessRequest;
  panel: ApprovalPanelState;
  onDatasetNameChange: (name: string) => void;
  onApprove: () => void;
  onCancel: () => void;
}

function ApprovalPanel({
  request,
  panel,
  onDatasetNameChange,
  onApprove,
  onCancel,
}: ApprovalPanelProps) {
  const isStreaming = panel.streamStatus === "streaming";
  const isDone = panel.streamStatus === "done";
  const showProgress = panel.streamStatus !== "idle";

  return (
    <div className="px-4 py-4 space-y-3">
      <div className="rounded-lg border border-border bg-card p-4 space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor={`dataset-name-${request.id}`} className="font-semibold">
            Dataset name
          </Label>
          <Input
            id={`dataset-name-${request.id}`}
            value={panel.datasetName}
            onChange={(e) => onDatasetNameChange(e.target.value)}
            disabled={isStreaming || isDone}
            className="border-amber-400 focus-visible:ring-amber-400"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={onApprove}
            disabled={isStreaming || isDone || !panel.datasetName.trim()}
            className="bg-green-700 hover:bg-green-800 text-white"
          >
            Approve &amp; export →
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={isStreaming}>
            Close
          </Button>
        </div>
      </div>

      {showProgress && (
        <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30 px-4 py-3 space-y-2">
          <p className="text-sm font-semibold text-green-800 dark:text-green-400">
            Export progress
          </p>
          <ul className="space-y-1.5">
            {panel.progressItems.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                {item.status === "loading" ? (
                  <Loader2
                    className="mt-0.5 size-4 shrink-0 animate-spin text-muted-foreground"
                    aria-hidden
                  />
                ) : (
                  <Check
                    className="mt-0.5 size-4 shrink-0 text-green-600 dark:text-green-400"
                    aria-hidden
                  />
                )}
                <span
                  className={cn(
                    item.status === "done" ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {item.message}
                </span>
              </li>
            ))}
            {panel.streamStatus === "error" && panel.streamError && (
              <li className="flex items-start gap-2 text-sm">
                <X className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
                <span className="text-destructive">{panel.streamError}</span>
              </li>
            )}
          </ul>
          {isDone && panel.resultDatasetId && (
            <a
              href={`/nextgen/datasets/${panel.resultDatasetId}`}
              className="inline-flex items-center gap-1 rounded-lg border border-green-600 px-3 py-1.5 text-sm font-semibold text-green-700 hover:bg-green-100 dark:text-green-400 dark:border-green-600 dark:hover:bg-green-900/30"
            >
              Open dataset in catalog →
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export const DatasetAccessRequests = () => {
  const { datasetId } = useParams();
  const { showError, showSuccess } = useToast();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<AccessRequestStatus | null>(null);

  const params = new URLSearchParams();
  if (datasetId) params.set("parent_dataset_id", datasetId);
  if (statusFilter) params.set("status_filter", statusFilter);
  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String((page - 1) * PAGE_SIZE));

  function handleStatusFilterChange(filter: AccessRequestStatus | null) {
    setStatusFilter(filter);
    setPage(1);
  }

  const { data, isLoading, mutate } = useFetcher<PaginatedAccessRequests>(
    datasetId ? `/api/cohort-explorer/v1/access-requests?${params.toString()}` : null
  );

  const requests = data?.items ?? [];
  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  // AbortController and submission guard must survive re-renders without triggering them — legitimate ref use.
  const abortRef = useRef<AbortController | null>(null);
  const isSubmittingRef = useRef(false);

  const [approvalPanel, setApprovalPanel] = useState<ApprovalPanelState | null>(null);
  const [confirmDeny, setConfirmDeny] = useState<CohortAccessRequest | null>(null);
  const [isDenying, setIsDenying] = useState(false);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  function handleApproveClick(request: CohortAccessRequest) {
    setApprovalPanel({
      requestId: request.id,
      datasetName: `Cohort ${request.cohort_id}`,
      streamStatus: "idle",
      progressItems: [],
      resultDatasetId: null,
      streamError: null,
    });
  }

  function handleCancelApproval() {
    abortRef.current?.abort();
    setApprovalPanel(null);
  }

  async function handleApproveSubmit() {
    if (!approvalPanel || isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setApprovalPanel((prev) =>
      prev ? { ...prev, streamStatus: "streaming", progressItems: [], streamError: null } : null
    );

    try {
      const response = await apiStreamClient(
        `/api/cohort-explorer/v1/access-requests/${approvalPanel.requestId}/approve`,
        {
          method: "POST",
          body: JSON.stringify({ name: approvalPanel.datasetName.trim() }),
          signal: controller.signal,
        }
      );

      let succeeded = false;
      let progressReceived = false;

      for await (const event of readStreamEvents(response, controller.signal)) {
        const data = event as StreamEvent;
        if (data.kind === "progress") {
          progressReceived = true;
          setApprovalPanel((prev) => {
            if (!prev) return null;
            const updated = prev.progressItems.map((item, i) =>
              i === prev.progressItems.length - 1 ? { ...item, status: "done" as const } : item
            );
            return {
              ...prev,
              progressItems: [...updated, { message: data.message, status: "loading" }],
            };
          });
        } else if (data.kind === "result") {
          setApprovalPanel((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              progressItems: prev.progressItems.map((item) => ({
                ...item,
                status: "done" as const,
              })),
              resultDatasetId: data.id,
            };
          });
          succeeded = true;
        } else if (data.kind === "error") {
          throw new Error(data.message);
        }
      }

      if (succeeded) {
        setApprovalPanel((prev) => (prev ? { ...prev, streamStatus: "done" } : null));
        await mutate();
      } else {
        throw new Error(
          progressReceived
            ? "The export did not complete. The server may have encountered an issue — please try again."
            : "The server did not respond. Please try again."
        );
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      let streamError: string;
      if (err instanceof TypeError) {
        streamError =
          "The export was interrupted by a network error. Check your connection and try again.";
      } else if (isApiError(err)) {
        streamError = getApiErrorMessage(err);
      } else {
        streamError = err instanceof Error ? err.message : "An unexpected error occurred.";
      }
      setApprovalPanel((prev) => (prev ? { ...prev, streamStatus: "error", streamError } : null));
    } finally {
      isSubmittingRef.current = false;
    }
  }

  async function handleDeny() {
    if (!confirmDeny) return;
    setIsDenying(true);
    try {
      await apiMutate(
        `/api/cohort-explorer/v1/access-requests/${confirmDeny.id}/deny`,
        "POST",
        undefined,
        {
          expects204: true,
        }
      );
      await mutate();
      showSuccess("Request denied successfully");
    } catch {
      showError("Failed to deny request");
    } finally {
      setIsDenying(false);
      setConfirmDeny(null);
    }
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-semibold">Access Requests</h2>
          <p className="text-sm text-muted-foreground">
            Manage access requests for cohort data from users with preview access to this dataset.
          </p>
        </div>

        <div className="flex items-center gap-1">
          {(
            [null, "pending", "processing", "approved", "denied", "failed", "cancelled"] as const
          ).map((filter) => (
            <Button
              key={filter ?? "all"}
              variant={statusFilter === filter ? "secondary" : "ghost"}
              size="sm"
              onClick={() => handleStatusFilterChange(filter)}
            >
              {filter ? filter.charAt(0).toUpperCase() + filter.slice(1) : "All"}
            </Button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : requests && requests.length > 0 ? (
          <div className="rounded-lg border border-border">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[15%]">Requested By</TableHead>
                  <TableHead className="w-[8%]">Cohort</TableHead>
                  <TableHead className="w-[20%]">Intended Use</TableHead>
                  <TableHead className="w-[10%]">Status</TableHead>
                  <TableHead className="w-[24%]">Child Dataset Id</TableHead>
                  <TableHead className="w-[13%]">Date</TableHead>
                  <TableHead className="w-[10%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <Fragment key={request.id}>
                    <TableRow>
                      <TableCell className="font-medium">{request.created_by}</TableCell>
                      <TableCell>{request.cohort_id}</TableCell>
                      <TableCell className="whitespace-normal break-words">
                        {request.intended_use}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[request.status]} className="capitalize">
                          {request.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {request.child_dataset_id ? (
                          <a
                            href={`/nextgen/datasets/${request.child_dataset_id}`}
                            className="text-foreground underline hover:no-underline"
                          >
                            {request.child_dataset_id}
                          </a>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(request.created_at)}
                      </TableCell>
                      <TableCell>
                        {(request.status === "pending" || request.status === "failed") && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-700 hover:text-green-800"
                              onClick={() => handleApproveClick(request)}
                            >
                              {request.status === "failed" ? (
                                <RefreshCw className="size-4" />
                              ) : (
                                <CheckCircle className="size-4" />
                              )}
                              {request.status === "failed" ? "Retry" : "Approve"}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive/80"
                              onClick={() => setConfirmDeny(request)}
                            >
                              <XCircle className="size-4" />
                              Deny
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    {approvalPanel?.requestId === request.id && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={7} className="p-0">
                          <ApprovalPanel
                            request={request}
                            panel={approvalPanel}
                            onDatasetNameChange={(name) =>
                              setApprovalPanel((prev) =>
                                prev ? { ...prev, datasetName: name } : null
                              )
                            }
                            onApprove={() => void handleApproveSubmit()}
                            onCancel={handleCancelApproval}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              {statusFilter
                ? `No ${statusFilter} access requests for this dataset.`
                : "No cohort access requests for this dataset."}
            </p>
          </div>
        )}

        {data && data.total > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.total)} of{" "}
              {data.total}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="size-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <AlertDialog
        open={confirmDeny !== null}
        onOpenChange={(open) => !open && !isDenying && setConfirmDeny(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deny Access Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deny the access request from{" "}
              <strong>{confirmDeny?.created_by}</strong> for cohort{" "}
              <strong>{confirmDeny?.cohort_id}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDenying}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeny}
              disabled={isDenying}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDenying ? "Processing…" : "Deny"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
